/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Organizational Impact Calculator - HR, TPRM, and Auditing impact on game metrics
 */

import { Employee, Vendor, AuditStatus, ComplianceChecklist } from '../types';

export interface OrgImpactMetrics {
  hrComplianceBonus: number; // Compliance boost from HR hiring
  hrThreatReduction: number; // Threat reduction from trained staff
  tprmThreatPenalty: number; // Threat from vendor risks
  tprmComplianceBoost: number; // Compliance from vendor management
  auditComplianceBoost: number; // Compliance boost from completed audits
  auditThreatReduction: number; // Threat reduction from audit findings remediation
  totalOrgImpact: {
    complianceModifier: number; // -50 to +50
    threatModifier: number; // -50 to +50
  };
}

/**
 * Calculate HR impact on compliance and threat
 * - More employees = better compliance culture
 * - Better trained staff = lower threat
 * - High morale = better compliance
 */
export const calculateHRImpact = (employees: Employee[]): { complianceBonus: number; threatReduction: number } => {
  if (employees.length === 0) {
    return { complianceBonus: 0, threatReduction: 0 };
  }

  // Headcount bonus: +1 compliance per 5 employees
  const headcountBonus = Math.floor(employees.length / 5);

  // Training bonus: +2 compliance per trained employee
  const trainingBonus = employees.filter(e => e.trainingLevel > 0).length * 2;

  // Morale bonus: +1 compliance per 10 points of avg morale above 50
  const avgMorale = employees.reduce((sum, e) => sum + e.morale, 0) / employees.length;
  const moraleBonus = Math.max(0, Math.floor((avgMorale - 50) / 10));

  // Threat reduction from security training
  const securityTrainedCount = employees.filter(e => 
    e.type.includes('Security') || e.type.includes('Compliance') || e.trainingLevel >= 2
  ).length;
  const threatReduction = securityTrainedCount * 2;

  const totalComplianceBonus = headcountBonus + trainingBonus + moraleBonus;

  return {
    complianceBonus: Math.min(50, totalComplianceBonus), // Cap at +50
    threatReduction: Math.min(30, threatReduction) // Cap at -30
  };
};

/**
 * Calculate TPRM (Third-Party Risk Management) impact
 * - Unmanaged vendors = threat increase
 * - Well-managed vendors = compliance boost
 * - High-risk vendors = significant threat
 */
export const calculateTPRMImpact = (vendors: Vendor[], workflows: any[]): { threatPenalty: number; complianceBoost: number } => {
  if (vendors.length === 0) {
    return { threatPenalty: 0, complianceBoost: 0 };
  }

  // Risk penalty: +1 threat per vendor with risk score > 60
  const highRiskVendors = vendors.filter(v => v.riskScore > 60).length;
  const riskThreatPenalty = highRiskVendors * 2;

  // Critical vendor penalty: +3 threat per critical vendor with poor SLA
  const criticalRiskVendors = vendors.filter(v => 
    v.criticality === 'Critical' && v.slaCompliance < 85
  ).length;
  const criticalThreatPenalty = criticalRiskVendors * 3;

  // Compliance boost from managed vendors
  const managedVendors = vendors.filter(v => v.status === 'Active' && v.riskScore < 50).length;
  const managedComplianceBoost = managedVendors * 1;

  // Workflow progress bonus
  const completedWorkflows = workflows.filter((w: any) => w.stage === 'ActiveMonitoring').length;
  const workflowBonus = completedWorkflows * 2;

  const totalThreatPenalty = Math.min(40, riskThreatPenalty + criticalThreatPenalty);
  const totalComplianceBoost = Math.min(25, managedComplianceBoost + workflowBonus);

  return {
    threatPenalty: totalThreatPenalty,
    complianceBoost: totalComplianceBoost
  };
};

/**
 * Calculate Auditing impact on compliance
 * - Completed audits = major compliance boost
 * - Audit findings = threat increase if not remediated
 * - Overdue audits = compliance penalty
 */
export const calculateAuditImpact = (audits: AuditStatus[], checklists: ComplianceChecklist[]): { complianceBoost: number; threatPenalty: number } => {
  let complianceBoost = 0;
  let threatPenalty = 0;

  // Completed audits: +5 compliance each
  const completedAudits = audits.filter(a => a.status === 'Completed').length;
  complianceBoost += completedAudits * 5;

  // Audit score bonus: +1 compliance per 10 points of avg score
  const completedAuditScores = audits.filter(a => a.status === 'Completed').map(a => a.score);
  if (completedAuditScores.length > 0) {
    const avgScore = completedAuditScores.reduce((a, b) => a + b, 0) / completedAuditScores.length;
    complianceBoost += Math.floor(avgScore / 10);
  }

  // Critical findings: +2 threat each (unresolved)
  const criticalFindings = audits.reduce((sum, a) => sum + a.findings.critical, 0);
  threatPenalty += criticalFindings * 2;

  // High findings: +1 threat each
  const highFindings = audits.reduce((sum, a) => sum + a.findings.high, 0);
  threatPenalty += highFindings * 1;

  // Overdue audits: -5 compliance each
  const overdueAudits = audits.filter(a => a.status === 'NotStarted' && a.targetDate && a.targetDate < Date.now()).length;
  complianceBoost -= overdueAudits * 5;

  // Completed checklists: +3 compliance each
  const completedChecklists = checklists.filter(c => c.status === 'Completed').length;
  complianceBoost += completedChecklists * 3;

  // Overdue checklists: -2 compliance each
  const overdueChecklists = checklists.filter(c => c.status === 'Overdue').length;
  complianceBoost -= overdueChecklists * 2;

  return {
    complianceBoost: Math.max(-50, Math.min(50, complianceBoost)), // Cap at ±50
    threatPenalty: Math.min(40, threatPenalty) // Cap at +40
  };
};

/**
 * Calculate total organizational impact on game metrics
 */
export const calculateTotalOrgImpact = (
  employees: Employee[],
  vendors: Vendor[],
  audits: AuditStatus[],
  checklists: ComplianceChecklist[],
  workflows: any[]
): OrgImpactMetrics => {
  const hrImpact = calculateHRImpact(employees);
  const tprmImpact = calculateTPRMImpact(vendors, workflows);
  const auditImpact = calculateAuditImpact(audits, checklists);

  const totalComplianceModifier = hrImpact.complianceBonus + tprmImpact.complianceBoost + auditImpact.complianceBoost;
  const totalThreatModifier = -(hrImpact.threatReduction) + tprmImpact.threatPenalty + auditImpact.threatPenalty;

  return {
    hrComplianceBonus: hrImpact.complianceBonus,
    hrThreatReduction: hrImpact.threatReduction,
    tprmThreatPenalty: tprmImpact.threatPenalty,
    tprmComplianceBoost: tprmImpact.complianceBoost,
    auditComplianceBoost: auditImpact.complianceBoost,
    auditThreatReduction: 0, // Audit threat is from findings, not reduction
    totalOrgImpact: {
      complianceModifier: Math.max(-50, Math.min(50, totalComplianceModifier)),
      threatModifier: Math.max(-50, Math.min(50, totalThreatModifier))
    }
  };
};

/**
 * Get organizational impact summary for UI display
 */
export const getOrgImpactSummary = (impact: OrgImpactMetrics): string[] => {
  const summary: string[] = [];

  if (impact.hrComplianceBonus > 0) {
    summary.push(`✓ HR: +${impact.hrComplianceBonus} compliance from ${Math.floor(impact.hrComplianceBonus / 2)} trained employees`);
  }
  if (impact.hrThreatReduction > 0) {
    summary.push(`✓ Security Training: -${impact.hrThreatReduction} threat from trained staff`);
  }
  if (impact.tprmComplianceBoost > 0) {
    summary.push(`✓ TPRM: +${impact.tprmComplianceBoost} compliance from managed vendors`);
  }
  if (impact.tprmThreatPenalty > 0) {
    summary.push(`⚠ Vendor Risk: +${impact.tprmThreatPenalty} threat from unmanaged vendors`);
  }
  if (impact.auditComplianceBoost > 0) {
    summary.push(`✓ Audits: +${impact.auditComplianceBoost} compliance from completed audits`);
  }
  if (impact.auditThreatReduction > 0) {
    summary.push(`⚠ Audit Findings: +${impact.auditThreatReduction} threat from critical findings`);
  }

  return summary;
};
